import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

export interface RouteGuardConfig {
  /** i18n key for dialog title, defaults to 'common.leave_title' */
  title?: string
  /** i18n key for dialog message, defaults to 'common.leave_text' */
  message?: string
}

export const useRouteGuardStore = defineStore('routeGuard', () => {
  const isEngaged = ref(false)
  const config = ref<RouteGuardConfig>({})
  const pendingResolve = ref<((canLeave: boolean) => void) | null>(null)

  function engageRouteGuard(options: RouteGuardConfig = {}) {
    isEngaged.value = true
    config.value = options
  }

  function disengageRouteGuard() {
    isEngaged.value = false
    config.value = {}

    if (pendingResolve.value != null) {
      pendingResolve.value(false)
      pendingResolve.value = null
    }
  }

  function setPendingNavigation(resolve: (canLeave: boolean) => void) {
    if (pendingResolve.value != null) {
      try {
        pendingResolve.value(false)
      } catch {
        // Ignore errors from previous resolver
      }
    }
    pendingResolve.value = resolve
  }

  function confirmLeave() {
    if (pendingResolve.value != null) {
      pendingResolve.value(true)
      pendingResolve.value = null
    }
    isEngaged.value = false
    config.value = {}
  }

  function cancelLeave() {
    if (pendingResolve.value != null) {
      pendingResolve.value(false)
      pendingResolve.value = null
    }
  }

  const showDialog = computed(() => isEngaged.value && pendingResolve.value != null)
  const dialogTitle = computed(() => config.value.title ?? 'common.leave_title')
  const dialogMessage = computed(() => config.value.message ?? 'common.leave_text')

  return {
    isEngaged,
    config,
    showDialog,
    dialogTitle,
    dialogMessage,
    engageRouteGuard,
    disengageRouteGuard,
    setPendingNavigation,
    confirmLeave,
    cancelLeave,
  }
})
